import React, { useState, useEffect, useCallback } from 'react';
import { AppTopNav } from 'app/modules/shared/AppTopNav';
import { BasePageProps } from 'app/types';
import { AdminUserManualService, UserManualInfo } from 'app/services/AdminUserManualService';
import { useDropzone } from 'react-dropzone';
import { Copy } from 'react-bootstrap-icons';
import { Tooltip, OverlayTrigger } from 'react-bootstrap';

const MAX_SIZE = 20 * 1024 * 1024;

export const AdminUserManuals: React.FC<BasePageProps> = (props: BasePageProps) => {
  const service = new AdminUserManualService();
  const [manuals, setManuals] = useState<UserManualInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploadingKey, setUploadingKey] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const m = await service.getManuals();
      setManuals(m);
    } catch (err: any) {
      setError(err.message || 'Failed to load manuals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onDrop = useCallback(async (acceptedFiles: File[], key: string) => {
    const file = acceptedFiles[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      setError('Only PDF files are accepted');
      return;
    }
    if (file.size > MAX_SIZE) {
      setError('File exceeds 20MB limit');
      return;
    }
    setUploadingKey(key);
    setUploadProgress(0);
    setError(null);
    try {
      await service.uploadManual(key, file, (pct) => setUploadProgress(pct));
      await load();
    } catch (err: any) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploadingKey(null);
    }
  }, []);

  const handleDelete = async (key: string) => {
    if (!window.confirm('Remove this manual from S3?')) return;
    setError(null);
    try {
      await service.deleteManual(key);
      await load();
    } catch (err: any) {
      setError(err.message || 'Delete failed');
    }
  };

  const copyToClipboard = async (url: string, key: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 2000);
    } catch {
      setError('Failed to copy URL');
    }
  };

  return (
    <div className='chatPage'>
      <AppTopNav title={props.title} breadcrumbs={props.breadcrumbs} />
      <div className='container-fluid mt-10'>
        <div className='card mb-5 mb-xl-8'>
          <div className='card-header border-0 pt-5'>
            <h3 className='card-title align-items-start flex-column'>
              <span className='card-label fw-bold fs-3 mb-1'>User Manuals</span>
              <span className='text-muted mt-1 fw-semibold fs-7'>
                Upload PDFs to S3 for public access via email links
              </span>
            </h3>
          </div>
          <div className='card-body py-3'>
            {error && (
              <div className='alert alert-danger d-flex align-items-center'>
                <i className='fas fa-exclamation-triangle me-2' />
                {error}
                <button className='btn-close ms-auto' onClick={() => setError(null)} />
              </div>
            )}
            {loading && <div className='fw-bold py-5'>Loading ...</div>}
            {!loading && (
              <div className='table-responsive'>
                <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                  <thead>
                    <tr className='fw-bold text-muted'>
                      <th className='min-w-200px'>Manual</th>
                      <th className='min-w-100px'>Status</th>
                      <th className='min-w-100px'>Last Updated</th>
                      <th className='min-w-150px'>Public URL</th>
                      <th className='min-w-150px text-end'>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {manuals.map((m) => (
                      <tr key={m.key}>
                        <td>
                          <div className='d-flex align-items-center'>
                            <i className='fas fa-file-pdf text-danger fs-2 me-3' />
                            <div>
                              <div className='fw-bold'>{m.label}</div>
                              <div className='text-muted fs-7'>{m.filename}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          {uploadingKey === m.key ? (
                            <div className='d-flex align-items-center'>
                              <div className='spinner-border spinner-border-sm me-2' />
                              <span>{uploadProgress}%</span>
                            </div>
                          ) : (
                            <span className={`badge badge-light-${m.exists ? 'success' : 'danger'}`}>
                              {m.exists ? 'Uploaded' : 'Not Uploaded'}
                            </span>
                          )}
                          {uploadingKey === m.key && (
                            <div className='progress mt-2' style={{ height: '4px' }}>
                              <div
                                className='progress-bar'
                                style={{ width: `${uploadProgress}%` }}
                              />
                            </div>
                          )}
                        </td>
                        <td className='text-muted fs-7'>
                          {m.lastModified ? new Date(m.lastModified).toLocaleDateString() : '-'}
                        </td>
                        <td>
                          {m.exists ? (
                            <div className='d-flex align-items-center gap-2'>
                              <code className='text-truncate d-inline-block' style={{ maxWidth: '200px' }}>
                                {m.publicUrl}
                              </code>
                              <OverlayTrigger
                                placement='top'
                                overlay={
                                  <Tooltip>{copiedKey === m.key ? 'Copied!' : 'Copy URL'}</Tooltip>
                                }
                              >
                                <button
                                  className='btn btn-icon btn-sm btn-light-primary'
                                  onClick={() => copyToClipboard(m.publicUrl, m.key)}
                                >
                                  <Copy />
                                </button>
                              </OverlayTrigger>
                            </div>
                          ) : (
                            <span className='text-muted'>—</span>
                          )}
                        </td>
                        <td className='text-end'>
                          <UploadButton
                            label={m.label}
                            onDrop={(files) => onDrop(files, m.key)}
                            disabled={uploadingKey !== null}
                          />
                          {m.exists && (
                            <button
                              className='btn btn-icon btn-bg-light btn-active-color-danger btn-sm ms-1'
                              disabled={uploadingKey !== null}
                              onClick={() => handleDelete(m.key)}
                            >
                              <i className='fas fa-trash' />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

function UploadButton ({ label, onDrop, disabled }: { label: string; onDrop: (files: File[]) => void; disabled: boolean }) {
  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    accept: '.pdf' as any,
    maxSize: MAX_SIZE,
    multiple: false,
    disabled,
    noClick: true,
  });

  return (
    <div {...getRootProps()} style={{ display: 'inline-block' }}>
      <input {...getInputProps()} />
      <button
        className={`btn btn-icon btn-bg-light btn-active-color-primary btn-sm ${disabled ? 'disabled' : ''}`}
        onClick={open}
        disabled={disabled}
        title={`Upload ${label}`}
      >
        <i className='fas fa-upload' />
      </button>
    </div>
  );
}
